import { CardRecord, MyIELTSDatabase, SrsFeedback, defaultDb } from '../db/indexedDb';
import { ReviewOptions } from './review';
import { reviewCard, type PersistedReview } from './reviewDb';
import { onPracticeComplete } from '../sync';

export interface SessionSnapshot {
  currentIndex: number;
  total: number;
  completed: number;
  remaining: number;
  skipped: number;
  finished: boolean;
  currentCard?: CardRecord;
}

export interface SessionSubmitResult {
  outcome: PersistedReview;
  snapshot: SessionSnapshot;
}

/** Session 持久化状态（保存到 sessionStorage） */
export interface SessionPersistState {
  cardIds: string[];
  index: number;
  skipped: number;
  submittedIndexes: number[];
  typeFilter?: string;
  isMixed?: boolean;
  savedAt: number;
}

const SESSION_STORAGE_KEY = 'enpass_session_state';

/** 保存 session 状态到 sessionStorage */
export function persistSession(state: SessionPersistState): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage 可能不可用
  }
}

/** 从 sessionStorage 恢复 session 状态 */
export function loadPersistedSession(): SessionPersistState | null {
  try {
    const data = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!data) return null;
    const state = JSON.parse(data) as SessionPersistState;
    // 检查是否过期（超过 2 小时认为过期）
    if (Date.now() - state.savedAt > 2 * 60 * 60 * 1000) {
      clearPersistedSession();
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

/** 清除持久化的 session 状态 */
export function clearPersistedSession(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export class SessionController {
  private queue: CardRecord[];
  private db: MyIELTSDatabase;
  private index = 0;
  private skipped = 0;
  private submittedAt: Set<number> = new Set(); // 记录已提交过的卡片索引
  private typeFilter?: string;
  private isMixed?: boolean;

  constructor(cards: CardRecord[], db: MyIELTSDatabase = defaultDb, options?: { typeFilter?: string; isMixed?: boolean }) {
    this.queue = [...cards];
    this.db = db;
    this.typeFilter = options?.typeFilter;
    this.isMixed = options?.isMixed;
  }

  /** 从持久化状态恢复 */
  restoreFrom(state: SessionPersistState): boolean {
    // 验证 cardIds 是否匹配当前队列
    const currentIds = this.queue.map(c => c.id);
    if (state.cardIds.length !== currentIds.length) return false;

    // 检查队列是否一致（允许顺序不同但内容相同）
    const stateIdSet = new Set(state.cardIds);
    const currentIdSet = new Set(currentIds);
    if (stateIdSet.size !== currentIdSet.size) return false;
    for (const id of stateIdSet) {
      if (!currentIdSet.has(id)) return false;
    }

    // 如果队列顺序不同，按保存的顺序重新排列
    if (JSON.stringify(state.cardIds) !== JSON.stringify(currentIds)) {
      const cardMap = new Map(this.queue.map(c => [c.id, c]));
      this.queue = state.cardIds.map(id => cardMap.get(id)!).filter(Boolean);
    }

    this.index = state.index;
    this.skipped = state.skipped;
    this.submittedAt = new Set(state.submittedIndexes);
    return true;
  }

  /** 持久化当前状态 */
  persist(): void {
    const state: SessionPersistState = {
      cardIds: this.queue.map(c => c.id),
      index: this.index,
      skipped: this.skipped,
      submittedIndexes: Array.from(this.submittedAt),
      typeFilter: this.typeFilter,
      isMixed: this.isMixed,
      savedAt: Date.now(),
    };
    persistSession(state);
  }

  snapshot(): SessionSnapshot {
    const total = this.queue.length;
    const finished = this.index >= total;
    const completed = Math.min(this.index, total);
    const remaining = Math.max(total - completed, 0);

    return {
      currentIndex: this.index,
      total,
      completed,
      remaining,
      skipped: this.skipped,
      finished,
      currentCard: finished ? undefined : this.queue[this.index],
    };
  }

  current(): CardRecord | undefined {
    return this.queue[this.index];
  }

  async submit(feedback: SrsFeedback, options: ReviewOptions = {}): Promise<SessionSubmitResult> {
    const card = this.current();
    if (!card) {
      throw new Error('当前没有可复习的卡片');
    }

    const outcome = await reviewCard(card.id, feedback, options, this.db);
    this.queue[this.index] = outcome.card;
    this.submittedAt.add(this.index);
    this.index += 1;

    // 触发自动同步
    onPracticeComplete();

    // 持久化进度
    this.persist();

    // 如果完成了，清除持久化状态
    if (this.index >= this.queue.length) {
      clearPersistedSession();
    }

    return { outcome, snapshot: this.snapshot() };
  }

  /** 返回上一张卡片 */
  goBack(): SessionSnapshot {
    if (this.index > 0) {
      this.index -= 1;
    }
    this.persist();
    return this.snapshot();
  }

  /** 检查当前卡片是否已经提交过（用于判断是否需要跳过更新） */
  isCurrentSubmitted(): boolean {
    return this.submittedAt.has(this.index);
  }

  /** 仅前进索引，不记录为 skip（用于返回查看后无操作的情况） */
  advanceWithoutUpdate(): SessionSnapshot {
    if (this.index < this.queue.length) {
      this.index += 1;
    }
    this.persist();
    return this.snapshot();
  }

  skipCurrent(): SessionSnapshot {
    if (this.index < this.queue.length) {
      this.skipped += 1;
      this.index += 1;
    }
    this.persist();
    return this.snapshot();
  }
}
